import * as React from "react";
import { Card, CardContent } from "./card";
import { cn } from "../utils/cn";
import { useServiceHealth, type ServiceHealthConfig, type HealthStatus } from "../hooks/use-service-health";

function StatusIcon({ status }: { status: HealthStatus }) {
    if (status === "loading") {
        return (
            <svg
                className="text-muted-foreground h-5 w-5 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
            >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
        );
    }

    if (status === "healthy") {
        return (
            <svg
                className="h-5 w-5 text-green-600"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
            >
                <path
                    fillRule="evenodd"
                    d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                    clipRule="evenodd"
                />
            </svg>
        );
    }

    return (
        <svg
            className="h-5 w-5 text-red-600"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
        >
            <path
                fillRule="evenodd"
                d="M4.28 4.28a.75.75 0 011.06 0L10 8.94l4.66-4.66a.75.75 0 111.06 1.06L11.06 10l4.66 4.66a.75.75 0 11-1.06 1.06L10 11.06l-4.66 4.66a.75.75 0 01-1.06-1.06L8.94 10 4.28 5.34a.75.75 0 010-1.06z"
                clipRule="evenodd"
            />
        </svg>
    );
}

function ServiceStatusItem({ service }: { service: ServiceHealthConfig }) {
    const { status, message } = useServiceHealth(service);

    return (
        <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
                <StatusIcon status={status} />
                <span className="font-medium">{service.name}</span>
            </div>
            <span
                className={cn(
                    "text-sm",
                    status === "healthy" && "text-green-600",
                    status === "unhealthy" && "text-red-600",
                    status === "loading" && "text-muted-foreground",
                )}
            >
                {status === "loading" ? "Checking..." : message}
            </span>
        </div>
    );
}

export interface ServiceStatusProps extends React.HTMLAttributes<HTMLDivElement> {
    services: ServiceHealthConfig[];
}

const ServiceStatus = React.forwardRef<HTMLDivElement, ServiceStatusProps>(({ services, className, ...props }, ref) => (
    <Card ref={ref} className={cn("w-full", className)} {...props}>
        <CardContent className="space-y-4 p-6">
            <h3 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">Service Status</h3>
            {services.map((service) => (
                <ServiceStatusItem key={service.url} service={service} />
            ))}
        </CardContent>
    </Card>
));
ServiceStatus.displayName = "ServiceStatus";

export { ServiceStatus };
